/**
 * ChairShare — the shipped default theme.
 *
 * Every brand value below was observed on chairshare.us on 2026-09-02 (see
 * roadmap/01-foundation-and-conventions.md, "ChairShare default theme") and is
 * provisional until official brand assets arrive. Values marked DERIVED are ours:
 * they exist because the brand's dark-first marketing palette has no light-surface
 * equivalents and all three brand golds fail WCAG AA for body-size text on white
 * (≈ 2.1–3.1 : 1).
 *
 * Light-first mapping (roadmap recommendation): the staff app inverts the brand
 * neutrals — off-white / white surfaces, near-black text, warm greys for borders
 * and muted text — and keeps gold as the single accent for fills-with-dark-text,
 * borders, icons and large figures. Gold is never used for body-size text on
 * white; text links use the DERIVED dark gold `#845F22` (5.77:1 on white,
 * 5.10:1 on the off-white page). Dark mode is the brand palette as published.
 */
import { defineTheme } from "../types.js";

const gold = {
  "100": "#F0C67F", // hover (dark surfaces)
  "200": "#D9A657", // primary UI gold: buttons, borders, icons, links on dark
  "300": "#D8AC5A", // large display figures on dark surfaces
  "400": "#C99A4A", // eyebrow / label gold on dark surfaces
  "500": "#B48B45", // the logo mark; large figures on LIGHT surfaces (3.13:1 on white — AA large)
  "600": "#845F22", // DERIVED: text links + eyebrows on light surfaces (5.77:1 on white)
  "700": "#6B4C18", // DERIVED: link hover on light surfaces
} as const;

const warm = {
  "0": "#FFFFFF", // DERIVED: card surface in light mode
  "50": "#F3F1EA", // brand off-white (primary text on dark → page background on light)
  "100": "#E6E1D4", // DERIVED: hairline border on light surfaces
  "200": "#D8D3C4", // brand secondary text on dark → strong border on light
  "300": "#8A8578", // brand muted text on dark → faint text on light (3.4:1, decorative only)
  "400": "#6B6656", // brand faint text on dark → muted text on light (5.61:1)
  "500": "#5C584C", // brand faint text on dark → secondary text on light (7.10:1)
  "600": "#2A271F", // brand border (dark, strong)
  "700": "#221F18", // brand border (dark)
  "800": "#1C1913", // brand raised surface (dark)
  "850": "#151310", // brand raised surface (dark, alternate)
  "900": "#121110", // brand surface (dark)
  "950": "#0E0D0A", // brand sunken surface (dark)
  "1000": "#0B0A08", // brand page background (dark) / primary text on light
} as const;

export const chairshare = defineTheme({
  name: "chairshare",
  productName: "ChairShare",
  shortName: "ChairShare",
  legalName: "ChairShare",
  tagline: "Independent Ownership. Collective Power.",
  supportEmail: "support@chairshare.us",
  logo: {
    // Published lockup has a WHITE wordmark: usable on dark surfaces only.
    lockupDark: "https://chairshare.us/images/logo-lockup.png",
    // No light-surface lockup is published; the shell renders mark + text wordmark
    // in the display font until the client supplies one (interim, documented).
    lockupLight: null,
    mark: "https://chairshare.us/images/logo-mark.png",
    favicon: "https://chairshare.us/images/favicon-32.png",
    touchIcon: "https://chairshare.us/apple-touch-icon.png",
    themeColor: warm["1000"],
  },
  palette: {
    primary: gold,
    neutral: warm,
  },
  fonts: {
    display: {
      family: "Cormorant Garamond",
      stack: '"Cormorant Garamond", Georgia, "Times New Roman", serif',
    },
    body: {
      family: "Manrope",
      stack: 'Manrope, "Segoe UI", system-ui, -apple-system, Roboto, Helvetica, Arial, sans-serif',
    },
    mono: {
      family: null,
      stack: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
    },
    stylesheetHref:
      "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Manrope:wght@400;500;600;700&display=swap",
  },
  type: {
    body: "15px",
    xs: "12px",
    sm: "14px",
    md: "15px",
    lg: "17px",
    xl: "21px",
    "2xl": "26px",
    "3xl": "34px",
    display: "44px",
    leadingBody: "1.5",
    leadingTight: "1.3",
    trackingEyebrow: "0.02em",
    eyebrowTransform: "none",
  },
  radius: {
    sm: "2px",
    md: "2px",
    lg: "3px",
    pill: "999px",
  },
  spacing: {
    "1": "4px",
    "2": "8px",
    "3": "12px",
    "4": "16px",
    "5": "20px",
    "6": "24px",
    "7": "32px",
    "8": "40px",
    "9": "48px",
    "10": "64px",
  },
  modes: {
    light: {
      colorScheme: "light",
      bg: warm["50"],
      surface: warm["0"],
      surfaceRaised: warm["0"],
      surfaceSunken: warm["50"],
      border: warm["100"],
      borderStrong: warm["200"],
      text: warm["1000"],
      textSecondary: warm["500"],
      textMuted: warm["400"],
      textFaint: warm["300"],
      accent: gold["200"],
      accentHover: gold["400"],
      accentFg: warm["1000"],
      accentSoft: "rgba(217, 166, 87, 0.14)",
      accentBorder: "rgba(217, 166, 87, 0.55)",
      accentFigure: gold["500"],
      eyebrow: gold["600"],
      link: gold["600"],
      linkHover: gold["700"],
      focusRing: "rgba(217, 166, 87, 0.5)",
      rowHover: "rgba(11, 10, 8, 0.035)",
      overlay: "rgba(11, 10, 8, 0.45)",
      // Semantic colors are not part of the brand site; defined here (AA on white).
      semantic: {
        success: { fg: "#2E7D46", bg: "rgba(46, 125, 70, 0.10)", border: "rgba(46, 125, 70, 0.35)" },
        warning: { fg: "#9A4F00", bg: "rgba(154, 79, 0, 0.10)", border: "rgba(154, 79, 0, 0.35)" },
        danger: { fg: "#B3261E", bg: "rgba(179, 38, 30, 0.08)", border: "rgba(179, 38, 30, 0.35)" },
        info: { fg: "#2B5C8A", bg: "rgba(43, 92, 138, 0.10)", border: "rgba(43, 92, 138, 0.35)" },
      },
      // Brand: 1px borders, no shadows. Floating layers get one soft shadow only.
      shadowSm: "none",
      shadowMd: "0 12px 32px rgba(11, 10, 8, 0.12)",
    },
    dark: {
      colorScheme: "dark",
      bg: warm["1000"],
      surface: warm["900"],
      surfaceRaised: warm["800"],
      surfaceSunken: warm["950"],
      border: warm["700"],
      borderStrong: warm["600"],
      text: warm["50"],
      textSecondary: warm["200"],
      textMuted: warm["300"],
      textFaint: warm["400"],
      accent: gold["200"],
      accentHover: gold["100"],
      accentFg: warm["1000"],
      accentSoft: "rgba(217, 166, 87, 0.12)",
      accentBorder: "rgba(217, 166, 87, 0.45)",
      accentFigure: gold["300"],
      eyebrow: gold["400"],
      link: gold["200"],
      linkHover: gold["100"],
      focusRing: "rgba(240, 198, 127, 0.45)",
      rowHover: "rgba(243, 241, 234, 0.04)",
      overlay: "rgba(0, 0, 0, 0.6)",
      semantic: {
        success: { fg: "#7FCB8F", bg: "rgba(127, 203, 143, 0.12)", border: "rgba(127, 203, 143, 0.35)" },
        warning: { fg: "#E8955C", bg: "rgba(232, 149, 92, 0.12)", border: "rgba(232, 149, 92, 0.35)" },
        danger: { fg: "#F08A80", bg: "rgba(240, 138, 128, 0.12)", border: "rgba(240, 138, 128, 0.35)" },
        info: { fg: "#8AB4E8", bg: "rgba(138, 180, 232, 0.12)", border: "rgba(138, 180, 232, 0.35)" },
      },
      shadowSm: "none",
      shadowMd: "0 12px 32px rgba(0, 0, 0, 0.5)",
    },
  },
  email: {
    senderDisplayName: "ChairShare",
  },
});
